import { User } from '@botpress/webchat-client'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

type ParticipantStore = {
  participants: User[]
  setParticipants: (participants: User[]) => void
  addParticipant: (participant: User) => void
  removeParticipant: (participantId: string) => void
  clearParticipants: () => void
}

const createParticipantStore = (storeName: string) =>
  create<ParticipantStore>()(
    devtools(
      (set) => ({
        participants: [],
        setParticipants: (participants: User[]) => set({ participants }),
        addParticipant: (participant: User) => set((state) => ({ participants: [...state.participants, participant] })),
        removeParticipant: (participantId: string) =>
          set((state) => ({ participants: state.participants.filter((p) => p.id !== participantId) })),
        clearParticipants: () => set({ participants: [] }),
      }),
      { name: storeName }
    )
  )

type BoundParticipantStore = ReturnType<typeof createParticipantStore>

const participantStoreInstances: { [key: string]: BoundParticipantStore } = {}

export const getUseParticipantStore = (conversationId: string): BoundParticipantStore => {
  const storeKey = `participants-${conversationId}`

  if (!participantStoreInstances[storeKey]) {
    participantStoreInstances[storeKey] = createParticipantStore(storeKey)
  }

  return participantStoreInstances[storeKey]
}

/**
 * Clears all cached participant store instances.
 * Useful for testing to ensure clean state between test runs.
 */
export const clearParticipantStoreCache = () => {
  Object.keys(participantStoreInstances).forEach((key) => {
    delete participantStoreInstances[key]
  })
}
